import { Message, QuotedMessage, type InstagramReplyMode as MessengerReplyMode } from '../types/Conversation';
import { conversationHasMessengerMessages } from './whatsappComposerUtils';
import type { Conversation } from '../types/Conversation';

export type { MessengerReplyMode };

export interface MessengerComposerContext {
  replyMode: MessengerReplyMode | null;
  pendingCommentId?: string;
  pendingCommentMessageId?: string;
  availableReplyModes: MessengerReplyMode[];
  unrepliedCommentOptions: MessengerPendingCommentOption[];
}

export interface MessengerPendingCommentOption {
  messageId: string;
  commentId: string;
  commentText: string;
  postLabelKey: string;
  postCaption?: string;
  thumbnailUrl?: string;
  timestamp: string;
}

export const isMessengerCommentMessage = (message: Message): boolean => {
  const surface = message.channelContext?.surface;
  if (surface === 'comment') {
    return message.source === 'messenger';
  }

  return Boolean(message.channelContext?.commentId) && message.source === 'messenger';
};

export const conversationHasMessengerComments = (messages: Message[]): boolean =>
  messages.some(isMessengerCommentMessage);

export const resolveConversationHasMessengerComments = (
  conversation: Pick<Conversation, 'containsMessengerComments' | 'messages'>,
): boolean => Boolean(conversation.containsMessengerComments) || conversationHasMessengerComments(conversation.messages);

export const getMessengerCommentPreviewPrefix = (
  message: Message,
  labelForKey: (key: string) => string,
): string | null => {
  if (!isMessengerCommentMessage(message)) {
    return null;
  }

  return `[${labelForKey('messengerCommentBadge')}]`;
};

export const resolveMessengerCommentKey = (message: Message): string | undefined => {
  const commentId = message.channelContext?.commentId?.trim();
  if (commentId) {
    return commentId;
  }

  if (isMessengerCommentMessage(message)) {
    return message.id;
  }

  return undefined;
};

export const isMessengerCommentReplyTarget = (message: Message): boolean =>
  isMessengerCommentMessage(message) && !message.messengerCommentDeleted;

export const getMessengerPostLabelKey = (message: Message): string => {
  const preview = message.contentPreview;
  if (preview?.contentType?.trim().toUpperCase() === 'VIDEO') {
    return 'messengerContentPreviewVideo';
  }

  return 'messengerContentPreviewPost';
};

export const listMessengerCommentTargetMessages = (messages: Message[]): Message[] => {
  const comments: Message[] = [];
  const seenCommentIds = new Set<string>();

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const commentId = resolveMessengerCommentKey(message);
    if (!commentId || !isMessengerCommentReplyTarget(message) || seenCommentIds.has(commentId)) {
      continue;
    }

    seenCommentIds.add(commentId);
    comments.push(message);
  }

  return comments;
};

export const buildMessengerPendingCommentOption = (message: Message): MessengerPendingCommentOption => ({
  messageId: message.id,
  commentId: resolveMessengerCommentKey(message) ?? message.id,
  commentText: message.content.trim(),
  postLabelKey: getMessengerPostLabelKey(message),
  postCaption: message.contentPreview?.caption?.trim() || undefined,
  thumbnailUrl: message.contentPreview?.thumbnailUrl,
  timestamp: message.timestamp,
});

export const resolveMessengerCommentTargetMessage = (
  messages: Message[],
  options?: {
    quotedMessage?: Message | null;
    selectedCommentMessageId?: string | null;
  },
): Message | null => {
  const selectedCommentMessageId = options?.selectedCommentMessageId;
  if (selectedCommentMessageId) {
    const selected = messages.find(message => message.id === selectedCommentMessageId);
    if (selected && isMessengerCommentReplyTarget(selected)) {
      return selected;
    }

    return null;
  }

  return null;
};

export const resolveMessengerCommentQuotedMessageId = (
  replyMode: MessengerReplyMode | null,
  targetMessage: Message | null,
  quotedMessage?: Message | null,
): string | undefined => {
  if (messengerCommentReplyBlocksAttachments(replyMode) && targetMessage) {
    return targetMessage.id;
  }

  return quotedMessage?.id;
};

export const resolveMessengerComposerContext = (
  messages: Message[],
  quotedMessage?: Message | null,
  tenantDefaultReplyMode: 'public_comment' | 'private_comment' = 'public_comment',
  hasMessengerComments = false,
  selectedCommentMessageId?: string | null,
): MessengerComposerContext => {
  const commentReplyOptions = listMessengerCommentTargetMessages(messages)
    .map(buildMessengerPendingCommentOption);

  if (!conversationHasMessengerMessages(messages) && !hasMessengerComments) {
    return { replyMode: null, availableReplyModes: [], unrepliedCommentOptions: [] };
  }

  if (!selectedCommentMessageId) {
    return {
      replyMode: 'dm',
      availableReplyModes: ['dm'],
      unrepliedCommentOptions: commentReplyOptions,
    };
  }

  const targetMessage = resolveMessengerCommentTargetMessage(messages, {
    quotedMessage,
    selectedCommentMessageId,
  });
  const pendingComment = targetMessage
    ? {
      commentId: resolveMessengerCommentKey(targetMessage) ?? targetMessage.id,
      messageId: targetMessage.id,
    }
    : null;

  if (!pendingComment) {
    return {
      replyMode: 'dm',
      availableReplyModes: ['dm'],
      unrepliedCommentOptions: commentReplyOptions,
    };
  }

  const availableReplyModes: MessengerReplyMode[] = ['private_comment', 'public_comment'];
  const defaultReplyMode: MessengerReplyMode = tenantDefaultReplyMode;

  return {
    replyMode: defaultReplyMode,
    pendingCommentId: pendingComment.commentId,
    pendingCommentMessageId: pendingComment.messageId,
    availableReplyModes,
    unrepliedCommentOptions: commentReplyOptions,
  };
};

export const messengerCommentReplyBlocksAttachments = (
  mode: MessengerReplyMode | null,
): boolean => mode === 'private_comment' || mode === 'public_comment';

export const messengerComposerShowsReplyTarget = (
  selectedCommentMessageId: string | null | undefined,
  replyMode: MessengerReplyMode | null,
  context: MessengerComposerContext,
): boolean => {
  if (!selectedCommentMessageId) {
    return false;
  }

  if (replyMode !== 'public_comment' && replyMode !== 'private_comment') {
    return false;
  }

  return context.pendingCommentMessageId === selectedCommentMessageId;
};

export const getMessengerReplyModeLabelKey = (mode: MessengerReplyMode): string => {
  if (mode === 'public_comment') {
    return 'messengerReplyMode_public_comment';
  }

  if (mode === 'private_comment') {
    return 'messengerReplyMode_private_comment';
  }

  return 'messengerReplyMode_dm';
};

export const messageToQuotedMessage = (message: Message): QuotedMessage => ({
  id: message.id,
  sender: message.sender,
  content: message.content,
  messageType: message.messageType,
  fileName: message.fileName,
  mediaUrl: message.mediaUrl,
});

export const findMessengerReplyMessageForComment = (
  messages: Message[],
  commentMessage: Message,
): { message: Message; replyMode: 'public_comment' | 'private_comment' } | null => {
  const commentKey = resolveMessengerCommentKey(commentMessage);
  if (!commentKey) {
    return null;
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const candidate = messages[index];
    if (candidate.messengerPublicReplyCommentId === commentKey) {
      return { message: candidate, replyMode: 'public_comment' };
    }

    if (candidate.messengerPrivateReplyCommentId === commentKey) {
      return { message: candidate, replyMode: 'private_comment' };
    }
  }

  return null;
};

export const isMessengerPublicReplyMessage = (message: Message, messages: Message[] = []): boolean => {
  const replyMode = message.messengerReplyMode?.trim().toLowerCase();
  if (replyMode === 'private_comment' || replyMode === 'dm') {
    return false;
  }

  if (replyMode === 'public_comment') {
    return true;
  }

  if (message.messengerPrivateReplyCommentId?.trim()) {
    return false;
  }

  if (message.messengerPublicReplyCommentId?.trim()) {
    return true;
  }

  for (const comment of messages) {
    if (!isMessengerCommentMessage(comment)) {
      continue;
    }

    const reply = findMessengerReplyMessageForComment(messages, comment);
    if (reply?.message.id === message.id && reply.replyMode === 'public_comment') {
      return true;
    }
  }

  return false;
};

export const getMessengerComposerPlaceholderKey = (
  mode: MessengerReplyMode | null,
  options: {
    isInteractiveComposerOpen: boolean;
    attachmentCount: number;
  },
): string => {
  if (options.isInteractiveComposerOpen) {
    return 'interactiveComposerMessagePlaceholder';
  }

  if (options.attachmentCount > 1) {
    return 'addCaptionFirstOptional';
  }

  if (options.attachmentCount === 1) {
    return 'addCaptionOptional';
  }

  if (mode === 'public_comment') {
    return 'messengerComposerPlaceholderPublic';
  }

  if (mode === 'private_comment') {
    return 'messengerComposerPlaceholderPrivate';
  }

  if (mode === 'dm') {
    return 'messengerComposerPlaceholderDm';
  }

  return 'typeMessageOrAskAi';
};
